/* eslint-disable */
import {
  actions,
  ActionType,
  Hooks,
  IdType,
  makePropGetter,
  Row,
  TableHighlightRowProps,
  TableInstance,
  TableState,
} from 'react-table';
import React from 'react';
import classNames from 'classnames';
import { FixedPluginHook } from '../utils';

const pluginName = 'useRowHighlight';

/**
 *
 * @param props
 * @param meta
 */
const defaultGetHighlightRowProps = <T extends object>(
  props: Partial<TableHighlightRowProps>,
  meta: { instance: TableInstance; row: Row<T> },
) => {
  const hlClassName = meta.instance.manualHighlightRowClass || 'highlighted';
  const newClassName = classNames(
    meta.row.id === meta.instance.state.highlightedRowId ? hlClassName : '',
    props.className,
  );
  return [
    props,
    {
      onClick: (e: React.MouseEvent) => {
        meta.row.toggleHighlightedRow();
      },
      className: newClassName,
      style: {
        cursor: 'pointer',
      },
    },
  ];
};

export const useRowHighlight: FixedPluginHook = <T extends object>(hooks: Hooks<T>) => {
  hooks.getHighlightRowProps = [defaultGetHighlightRowProps];

  hooks.stateReducers.push(reducer);
  hooks.useInstance.push(useInstance);
  hooks.prepareRow.push(prepareRow);
};

useRowHighlight.pluginName = pluginName;
actions.toggleHighlightedRow = 'toggleHighlightedRow';

function reducer<D extends object>(
  newState: TableState<D>,
  action: ActionType,
  previousState?: TableState<D>,
  instance?: TableInstance<D>,
) {
  if (action.type === actions.init) {
    return {
      ...newState,
      highlightedRowId: null,
    };
  }

  if (action.type === actions.toggleHighlightedRow) {
    const { highlightedRowId } = action;
    return {
      ...newState,
      highlightedRowId: highlightedRowId,
    };
  }

  return newState;
}

function useInstance<D extends object>(instance: TableInstance<D>) {
  const { dispatch } = instance;

  const toggleHighlightedRow = React.useCallback(
    (id: IdType<D>) => {
      return dispatch({
        type: actions.toggleHighlightedRow,
        highlightedRowId: id,
      });
    },
    [dispatch],
  );

  Object.assign(instance, {
    toggleHighlightedRow,
  });
}

function prepareRow<D extends object>(row: Row<D>, { instance }: { instance: TableInstance<D> }) {
  row.toggleHighlightedRow = () => instance.toggleHighlightedRow(row.id);
  row.getHighlightRowProps = makePropGetter(instance.getHooks().getHighlightRowProps, { instance: instance, row });
}
